import { AssociateAccountCard } from "@/components/common/section-components/AssociateAccountCard";
import { PersonAddCancelButtons } from "@/components/common/section-components/PersonAddCancelButtons";
import { PersonDataForm } from "@/components/common/section-components/PersonDataForm";
import { StepperComponent } from "@/components/stepper/Stepper";
import { AppRoutes } from "@/constants/routes";
import {
  AuxEntity,
  getListadoEstadosCiviles,
  getListadoHobbies,
  getListadoNivelesEstudio,
  getListadoProfesiones,
  getListadoSexosBiologicos,
} from "@/services/aux-entities.service";
import { Flex } from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { FieldValue, FormProvider, useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { createPersonSchema } from "../schema/person.schema";
import { useDniValidationStore } from "@/store/dniValidationStore";
import { useEmailValidationStore } from "@/store/emailValidationStore";
import { useRoles } from "@/hooks/useRoles";
import { createPersonHandler } from "../schema/handler-functions";

export const PersonCreationPage = () => {
  const [isActive, setIsActive] = useState<boolean>(false);

  useEffect(() => {
    setIsActive(true);
  });
  const navigate = useNavigate();

  const [loadingPersons, setLoadingPersons] = useState<boolean>(false);
  const [loadingCreatePerson, setLoadingCreatePerson] =
    useState<boolean>(false);

  const [currentStep, setCurrentStep] = useState(0);

  const methods = useForm({
    resolver: zodResolver(createPersonSchema),
  });

  // Estado unificado para las entidades auxiliares y su carga
  const [auxEntities, setAuxEntities] = useState<{
    hobbies: AuxEntity[] | null;
    profesiones: AuxEntity[] | null;
    nivelesEstudio: AuxEntity[] | null;
    estadosCiviles: AuxEntity[] | null;
    sexosBiologicos: AuxEntity[] | null;
  }>({
    hobbies: null,
    profesiones: null,
    nivelesEstudio: null,
    estadosCiviles: null,
    sexosBiologicos: null,
  });

  const [loadingAuxEntities, setLoadingAuxEntities] = useState<boolean>(false);

  const fetchAuxEntities = async () => {
    setLoadingAuxEntities(true);
    try {
      const [
        hobbies,
        profesiones,
        nivelesEstudio,
        estadosCiviles,
        sexosBiologicos,
      ] = await Promise.all([
        getListadoHobbies({}),
        getListadoProfesiones({}),
        getListadoNivelesEstudio(),
        getListadoEstadosCiviles(),
        getListadoSexosBiologicos(),
      ]);

      setAuxEntities({
        hobbies,
        profesiones,
        nivelesEstudio,
        estadosCiviles,
        sexosBiologicos,
      });
    } finally {
      setLoadingAuxEntities(false);
    }
  };

  useEffect(() => {
    if (isActive) {
      fetchAuxEntities();
    }
  }, [isActive]);

  const handleCancel = () => {
    navigate(AppRoutes.PERSONS);
  };

  const handleCreatePerson = async () => {
    await createPersonHandler({
      getValues: methods.getValues,
      setLoadingCreatePerson,
      navigate,
      fetchPermissions,
    });
  };

  const stepFieldsMap: Record<number, FieldValue<any>[]> = {
    1: ["cuenta"],
  };

  const innerHandleAdd = async (_data: any, _event: any, omitStep = false) => {
    if (omitStep) {
      const fieldsToClear = stepFieldsMap[currentStep];
      for (const field of fieldsToClear) {
        methods.setValue(field, null);
      }
    }

    if (currentStep < 1) {
      setCurrentStep((prev) => prev + 1);
    } else {
      await handleCreatePerson();
    }
  };

  // Ahora definimos `handleAdd` para aceptar el omitStep y llamar al handleSubmit
  const handleAdd = (omitStep = false) => {
    methods.handleSubmit((data, event) =>
      innerHandleAdd(data, event, omitStep),
    )();
  };

  const handleBack = () => setCurrentStep((prev) => prev - 1);

  const { isValid: isValidDni } = useDniValidationStore();
  const { isValid: isValidEmail } = useEmailValidationStore();

  const [dni, nombre, apellido] = useWatch({
    name: ["dni", "nombre", "apellido"],
    control: methods.control,
  });

  const { fetchPermissions } = useRoles();

  return (
    <Flex flexDir="column" paddingTop="20px" gap="12px">
      <FormProvider {...methods}>
        <PersonAddCancelButtons
          steps={1}
          title="Agregar Persona"
          control={methods.control}
          handleAdd={handleAdd}
          handleCancel={handleCancel}
          handleBack={handleBack}
          currentStep={currentStep}
          addLabel="Agregar"
          loading={loadingAuxEntities || loadingCreatePerson}
        />
        {!loadingAuxEntities && (
          <StepperComponent
            currentStep={currentStep}
            setCurrentStep={setCurrentStep}
            disableStep={
              !dni || !nombre || !apellido || !isValidEmail || !isValidDni
            }
            steps={[
              {
                title: "Agregar Persona",
              },
              {
                title: "Asociar cuenta",
              },
            ]}
          />
        )}
        {currentStep === 0 && !loadingAuxEntities && (
          <PersonDataForm
            estadosCiviles={auxEntities.estadosCiviles}
            hobbies={auxEntities.hobbies}
            nivelesEstudio={auxEntities.nivelesEstudio}
            profesiones={auxEntities.profesiones}
            sexosBiologicos={auxEntities.sexosBiologicos}
            loadingAuxEntities={loadingAuxEntities}
            operation="CREATE"
          />
        )}
        {currentStep === 1 && !loadingAuxEntities && (
          <AssociateAccountCard
            loadingPersons={loadingPersons}
            setLoadingPersons={setLoadingPersons}
          />
        )}
      </FormProvider>
    </Flex>
  );
};
